import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import bookingApi from '../../../../src/api/bookingApi';

const {
  FiTrendingUp, FiShoppingBag, FiDollarSign, FiUsers,
  FiBarChart2, FiPieChart, FiCalendar, FiArrowUpRight, FiArrowDownRight, 
  FiBriefcase, FiClock, FiCheckCircle, FiXCircle, FiAlertCircle
} = FiIcons;

const MiniBarChart = ({ data, color = '#C9A84C' }) => {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div className="flex items-end gap-1 h-20">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center gap-1">
          <div
            className="w-full rounded-t-sm transition-all duration-500"
            style={{ height: `${(d.value / max) * 100}%`, backgroundColor: color, opacity: 0.85 }}
          />
          <span className="text-[7px] text-text-muted font-bold">{d.label}</span>
        </div>
      ))}
    </div>
  );
};

const DonutChart = ({ segments }) => {
  let cumulative = 0;
  const r = 40, cx = 50, cy = 50, stroke = 14;
  const circumference = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 100 100" className="w-32 h-32">
      {segments.map((seg, i) => {
        const dashArray = (seg.pct / 100) * circumference;
        const dashOffset = -(cumulative / 100) * circumference;
        cumulative += seg.pct;
        return (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none"
            stroke={seg.color} strokeWidth={stroke}
            strokeDasharray={`${dashArray} ${circumference}`}
            strokeDashoffset={dashOffset}
            style={{ transition: 'stroke-dasharray 0.6s ease' }}
          />
        );
      })}
      <circle cx={cx} cy={cy} r={r - stroke / 2 - 1} fill="white" />
    </svg>
  );
};

const RANGES = ['7D', '30D', '90D', '1Y'];

const SalesAnalyticsTab = ({ vendorData }) => {
  const [range, setRange] = useState('30D');
  const [bookingAnalytics, setBookingAnalytics] = useState({
    totalBookings: 0,
    confirmedBookings: 0,
    totalRevenue: 0,
    averageBookingValue: 0,
    conversionRate: 0,
    pendingBookings: 0,
    cancelledBookings: 0,
    completedBookings: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      try {
        // Fix: Use correct method name getMerchantAnalytics instead of getProviderAnalytics
        const rawAnalytics = await bookingApi.getMerchantAnalytics(vendorData?.owner_id || 'demo');
        
        // Map snake_case API response to camelCase for the component
        setBookingAnalytics({
          totalBookings: rawAnalytics.total_bookings || 0,
          pendingBookings: rawAnalytics.pending_bookings || 0,
          confirmedBookings: rawAnalytics.confirmed_bookings || 0,
          completedBookings: rawAnalytics.completed_bookings || 0,
          cancelledBookings: rawAnalytics.cancelled_bookings || 0,
          totalRevenue: rawAnalytics.total_revenue || 0,
          conversionRate: parseFloat(rawAnalytics.conversion_rate) || 0,
          averageBookingValue: rawAnalytics.total_bookings > 0 ? (rawAnalytics.total_revenue / rawAnalytics.total_bookings) : 0
        });
      } catch (error) {
        console.error('Error fetching booking analytics:', error);
      }
      setLoading(false);
    };
    
    fetchAnalytics();
  }, [vendorData]);

  const salesData = {
    '7D':  [{ label: 'M', value: 12 }, { label: 'T', value: 8 }, { label: 'W', value: 19 }, { label: 'T', value: 14 }, { label: 'F', value: 22 }, { label: 'S', value: 31 }, { label: 'S', value: 18 }],
    '30D': [{ label: 'W1', value: 48 }, { label: 'W2', value: 62 }, { label: 'W3', value: 55 }, { label: 'W4', value: 79 }],
    '90D': [{ label: 'Jan', value: 120 }, { label: 'Feb', value: 145 }, { label: 'Mar', value: 189 }],
    '1Y':  [{ label: 'Q1', value: 320 }, { label: 'Q2', value: 410 }, { label: 'Q3', value: 380 }, { label: 'Q4', value: 490 }],
  };

  const servicesData = {
    '7D':  [{ label: 'M', value: 5 }, { label: 'T', value: 3 }, { label: 'W', value: 7 }, { label: 'T', value: 6 }, { label: 'F', value: 9 }, { label: 'S', value: 12 }, { label: 'S', value: 8 }],
    '30D': [{ label: 'W1', value: 20 }, { label: 'W2', value: 25 }, { label: 'W3', value: 22 }, { label: 'W4', value: 30 }],
    '90D': [{ label: 'Jan', value: 60 }, { label: 'Feb', value: 75 }, { label: 'Mar', value: 85 }],
    '1Y':  [{ label: 'Q1', value: 150 }, { label: 'Q2', value: 180 }, { label: 'Q3', value: 170 }, { label: 'Q4', value: 210 }],
  };

  const topProducts = [
    { name: 'Celestial Crystal Urn', units: 34, revenue: 2380, pct: 38 },
    { name: 'Sacred Bloom Bouquet', units: 28, revenue: 1120, pct: 29 },
    { name: 'Memory Lantern', units: 21, revenue: 840, pct: 22 },
  ];

  const topServices = [
    { name: 'Traditional Funeral Package', units: 12, revenue: 54000, pct: 55 },
    { name: 'Memorial Consultation', units: 24, revenue: 0, pct: 30 }, // Free service
    { name: 'Grief Counseling Session', units: 18, revenue: 2160, pct: 15 },
  ];

  // Dynamic pie chart segments based on real data
  const chartTotal = Math.max(bookingAnalytics.totalBookings, 1);
  const chartSegments = [
    { label: 'Confirmed', pct: (bookingAnalytics.confirmedBookings / chartTotal) * 100 || 60, color: '#10B981', value: bookingAnalytics.confirmedBookings || 42 },
    { label: 'Pending', pct: (bookingAnalytics.pendingBookings / chartTotal) * 100 || 15, color: '#F59E0B', value: bookingAnalytics.pendingBookings || 8 },
    { label: 'Cancelled', pct: (bookingAnalytics.cancelledBookings / chartTotal) * 100 || 10, color: '#6B7280', value: bookingAnalytics.cancelledBookings || 4 },
    { label: 'Completed', pct: (bookingAnalytics.completedBookings / chartTotal) * 100 || 15, color: '#3B82F6', value: bookingAnalytics.completedBookings || 0 }
  ];

  const kpis = [
    { 
      label: 'Gross Revenue', 
      value: `$${((bookingAnalytics.totalRevenue || 56160) + ((vendorData?.totalSales || 0) * 42)).toLocaleString()}`, 
      delta: '+22%', up: true, icon: FiDollarSign, color: 'text-green-500', bg: 'bg-green-50' 
    },
    { 
      label: 'Products Sold', 
      value: vendorData?.totalSales || 0, 
      delta: '+12%', up: true, icon: FiShoppingBag, color: 'text-blue-500', bg: 'bg-blue-50' 
    },
    { 
      label: 'Services Booked', 
      value: bookingAnalytics.totalBookings || 54, 
      delta: '+35%', up: true, icon: FiBriefcase, color: 'text-purple-500', bg: 'bg-purple-50' 
    },
    { 
      label: 'Conversion Rate', 
      value: `${(bookingAnalytics.conversionRate || 77.8).toFixed(1)}%`, 
      delta: '+8%', up: true, icon: FiTrendingUp, color: 'text-amber-500', bg: 'bg-amber-50' 
    },
    { 
      label: 'Pending Bookings', 
      value: bookingAnalytics.pendingBookings || 8, 
      delta: '-15%', up: false, icon: FiClock, color: 'text-orange-500', bg: 'bg-orange-50' 
    },
    { 
      label: 'Avg Booking Value', 
      value: `$${(bookingAnalytics.averageBookingValue || 1337).toFixed(0)}`, 
      delta: '+18%', up: true, icon: FiDollarSign, color: 'text-green-500', bg: 'bg-green-50' 
    },
  ];

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {kpis.map((kpi, idx) => (
          <motion.div key={kpi.label} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.08 }}
            className="bg-white p-6 rounded-[28px] border border-brand-dark shadow-sm">
            <div className={`w-10 h-10 ${kpi.bg} ${kpi.color} rounded-2xl flex items-center justify-center mb-4`}>
              <SafeIcon icon={kpi.icon} className="w-5 h-5" />
            </div>
            <p className="text-2xl font-serif text-accent-navy">
              {loading ? <span className="inline-block w-4 h-4 border-2 border-accent-gold border-t-accent-navy rounded-full animate-spin" /> : kpi.value}
            </p>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">{kpi.label}</p>
              <span className={`text-[9px] font-black flex items-center gap-0.5 ${kpi.up ? 'text-green-500' : 'text-red-400'}`}>
                <SafeIcon icon={kpi.up ? FiArrowUpRight : FiArrowDownRight} className="w-3 h-3" />
                {kpi.delta}
              </span>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <SafeIcon icon={FiCalendar} className="text-text-muted w-4 h-4" />
        <span className="text-[10px] font-black uppercase tracking-widest text-text-muted">Period:</span>
        <div className="flex bg-brand-light border border-brand-dark rounded-xl p-1">
          {RANGES.map(r => (
            <button key={r} onClick={() => setRange(r)}
              className={`px-4 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${
                range === r ? 'bg-accent-navy text-white' : 'text-text-muted hover:text-accent-navy'
              }`}>{r}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white p-8 rounded-[32px] border border-brand-dark shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 bg-blue-50 text-blue-500 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiShoppingBag} className="w-4 h-4" />
            </div>
            <div>
              <h4 className="font-serif text-accent-navy text-sm">Products</h4>
              <p className="text-[9px] text-text-muted font-bold uppercase tracking-widest">Units Sold</p>
            </div>
          </div>
          <MiniBarChart data={salesData[range]} color="#3B82F6" />
        </div>

        <div className="bg-white p-8 rounded-[32px] border border-brand-dark shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 bg-purple-50 text-purple-500 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiBriefcase} className="w-4 h-4" />
            </div>
            <div>
              <h4 className="font-serif text-accent-navy text-sm">Services</h4>
              <p className="text-[9px] text-text-muted font-bold uppercase tracking-widest">Bookings Made</p>
            </div>
          </div>
          <MiniBarChart data={servicesData[range]} color="#A855F7" />
        </div>

        <div className="bg-white p-8 rounded-[32px] border border-brand-dark shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 bg-green-50 text-green-500 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiDollarSign} className="w-4 h-4" />
            </div>
            <div>
              <h4 className="font-serif text-accent-navy text-sm">Revenue</h4>
              <p className="text-[9px] text-text-muted font-bold uppercase tracking-widest">Gross Earnings</p>
            </div>
          </div>
          <MiniBarChart data={salesData[range]} color="#10B981" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-8 rounded-[32px] border border-brand-dark shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 bg-purple-50 text-purple-500 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiBarChart2} className="w-4 h-4" />
            </div>
            <h4 className="font-serif text-accent-navy">Top Performers</h4>
          </div>
          
          <h5 className="text-[10px] font-black tracking-widest text-text-muted uppercase mb-4">Top Services</h5>
          <div className="space-y-4 mb-8">
            {topServices.map((p, i) => (
              <div key={p.name}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] font-black text-purple-500 w-4">#{i + 1}</span>
                    <span className="text-xs font-bold text-accent-navy truncate max-w-[140px]">{p.name}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-bold text-accent-navy">${p.revenue.toLocaleString()}</span>
                    <span className="text-[9px] text-text-muted ml-1">({p.units} bookings)</span>
                  </div>
                </div>
                <div className="h-1.5 bg-brand-light rounded-full overflow-hidden">
                  <motion.div className="h-full rounded-full bg-purple-500" initial={{ width: 0 }} animate={{ width: `${p.pct}%` }} transition={{ duration: 0.6 }} />
                </div>
              </div>
            ))}
          </div>

          <h5 className="text-[10px] font-black tracking-widest text-text-muted uppercase mb-4">Top Products</h5>
          <div className="space-y-4">
            {topProducts.map((p, i) => (
              <div key={p.name}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] font-black text-blue-500 w-4">#{i + 1}</span>
                    <span className="text-xs font-bold text-accent-navy truncate max-w-[140px]">{p.name}</span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-bold text-accent-navy">${p.revenue.toLocaleString()}</span>
                    <span className="text-[9px] text-text-muted ml-1">({p.units} units)</span>
                  </div>
                </div>
                <div className="h-1.5 bg-brand-light rounded-full overflow-hidden">
                  <motion.div className="h-full rounded-full bg-blue-500" initial={{ width: 0 }} animate={{ width: `${p.pct}%` }} transition={{ duration: 0.6 }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white p-8 rounded-[32px] border border-brand-dark shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 bg-brand-dark/20 text-accent-navy rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiPieChart} className="w-4 h-4" />
            </div>
            <h4 className="font-serif text-accent-navy">Booking Status Distribution</h4>
          </div>
          
          <div className="flex flex-col md:flex-row items-center gap-8 md:mt-12">
            <DonutChart segments={chartSegments} />
            <div className="space-y-4 flex-1 w-full">
              {chartSegments.map(d => (
                <div key={d.label} className="flex items-center justify-between p-3 border border-brand-dark rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-4 h-4 rounded-full shadow-sm" style={{ backgroundColor: d.color }} />
                    <span className="text-sm font-bold text-accent-navy">{d.label}</span>
                  </div>
                  <span className="text-sm text-text-muted font-bold">{Math.round(d.pct)}% <span className="text-[10px] ml-1 font-normal opacity-75">({d.value})</span></span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-2xl border border-brand-dark text-center shadow-sm">
          <div className="w-8 h-8 bg-amber-50 text-amber-500 rounded-xl flex items-center justify-center mx-auto mb-2">
            <SafeIcon icon={FiClock} className="w-4 h-4" />
          </div>
          <p className="text-lg font-serif text-accent-navy">{loading ? '...' : (bookingAnalytics.pendingBookings || 8)}</p>
          <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">Pending</p>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-brand-dark text-center shadow-sm">
          <div className="w-8 h-8 bg-green-50 text-green-500 rounded-xl flex items-center justify-center mx-auto mb-2">
            <SafeIcon icon={FiCheckCircle} className="w-4 h-4" />
          </div>
          <p className="text-lg font-serif text-accent-navy">{loading ? '...' : (bookingAnalytics.confirmedBookings || 42)}</p>
          <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">Confirmed</p>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-brand-dark text-center shadow-sm">
          <div className="w-8 h-8 bg-gray-50 text-gray-500 rounded-xl flex items-center justify-center mx-auto mb-2">
            <SafeIcon icon={FiXCircle} className="w-4 h-4" />
          </div>
          <p className="text-lg font-serif text-accent-navy">{loading ? '...' : (bookingAnalytics.cancelledBookings || 4)}</p>
          <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">Cancelled</p>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-brand-dark text-center shadow-sm">
          <div className="w-8 h-8 bg-blue-50 text-blue-500 rounded-xl flex items-center justify-center mx-auto mb-2">
            <SafeIcon icon={FiAlertCircle} className="w-4 h-4" />
          </div>
          <p className="text-lg font-serif text-accent-navy">{loading ? '...' : `${((bookingAnalytics.conversionRate || 77.8)).toFixed(1)}%`}</p>
          <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">Conversion</p>
        </div>
      </div>
    </div>
  );
};

export default SalesAnalyticsTab;